"use client";

import { useAuthStore } from "@kaplan-labs/up-auth-api-client";
import {
  QueryClient,
  QueryClientProvider,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { getAllInventoryItems, logError, useOwner } from "@up/data";

import { network } from "./network";
import { CART_STATUS } from "./constants";
import { parseCheckoutError } from "./parse-checkout-error";

import type { Result, TCart, TCatalogueItem } from "@up/data";
import type { Dispatch, PropsWithChildren, SetStateAction } from "react";

type TCartStatus = (typeof CART_STATUS)[keyof typeof CART_STATUS];

export type CartContextType = {
  data?: TCart;
  inventory: Array<TCatalogueItem>;
  step: 0 | 1;
  status: TCartStatus;
  isAddingItem: boolean;
  isSubmitting: boolean;
  isApplyingCoupon: boolean;
  isValidating: boolean;
  addItemError: string | null;
  error?: Error;
  couponError?: string;
  checkoutErrorMessage?: string;
  submitAsCompleteDidError: boolean;
  hasBlockingErrors: boolean;
  didError: boolean;
  cartIsVisible: boolean;

  submit: () => void;
  idle: () => void;
  addItem: (newItemID: string) => Promise<unknown>;
  removeItem: (itemID: string) => Promise<unknown>;
  setCartIsVisible: Dispatch<SetStateAction<boolean>>;
  applyCoupon: (code: string) => Promise<void>;
  removeCoupon: (code: string) => Promise<unknown>;
  finalValidate: () => Promise<Result<TCart>>;
  handleSubmitAsFree: () => Promise<Result<string>>;
  navigateToStep: (step: 0 | 1) => void;
  updateCart: (cart: TCart) => void;
  setCouponCodeOnLoad: (code: string) => void;
};

export const CartContext = createContext<CartContextType>({
  inventory: [],
  step: 0,
  status: "unstarted",
  isAddingItem: false,
  isSubmitting: false,
  isApplyingCoupon: false,
  isValidating: false,
  addItemError: null,
  error: undefined,
  submitAsCompleteDidError: false,
  hasBlockingErrors: false,
  didError: false,
  cartIsVisible: false,

  submit: () => {
    /* empty */
  },
  idle: () => {
    /* empty */
  },
  addItem: async () => {
    /* empty */
  },
  removeItem: async () => {
    /* empty */
  },
  setCartIsVisible: () => {
    /* empty */
  },
  applyCoupon: async () => {
    /* empty */
  },
  removeCoupon: async () => {
    /* empty */
  },
  finalValidate: async () => {
    return {
      value: null,
      error: new Error(""),
    };
  },
  handleSubmitAsFree: async () => {
    return {
      value: null,
      error: new Error(""),
    };
  },
  navigateToStep: () => {
    /* empty */
  },
  updateCart: () => {
    /* empty */
  },
  setCouponCodeOnLoad: () => {
    /* empty */
  },
});

type CartProviderProps = PropsWithChildren<{
  baseURL: string;
  navigateToPath: (path: string) => void;
}>;

const CartProviderInner = ({
  children,
  navigateToPath,
}: Omit<CartProviderProps, "baseURL">) => {
  const auth = useAuthStore();
  const owner = useOwner();
  const queryClient = useQueryClient();

  const cartQueryKey = useMemo(
    () => ["checkout", "cart", owner?.contactId],
    [owner?.contactId],
  );

  /*
    UI State
    ░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░
  */

  const [cartIsVisible, setCartIsVisible] = useState(false);
  const [step, setStep] = useState<0 | 1>(0);
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);
  const [checkoutErrorMessage, setCheckoutErrorMessage] = useState<string>();
  const [couponError, setCouponError] = useState<string>();
  const [couponCodeOnLoad, setCouponCodeOnLoad] = useState<string>();

  /*
    Queries
    ░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░
  */

  const cartQuery = useQuery<TCart, Error>({
    queryKey: cartQueryKey,
    queryFn: async () => {
      const response = await network.fetchCart();
      return {
        ...response.data,
        lineItems: response.data.lineItems ?? [],
        errors: response.errors,
      };
    },
    enabled: !!auth.currentUser && !!owner,
    staleTime: Infinity,
    retry: false,
  });

  const inventoryQuery = useQuery<TCatalogueItem[]>({
    queryKey: ["checkout", "inventory"],
    enabled: !!auth.currentUser,
    queryFn: async () => {
      const items = await getAllInventoryItems();
      return items
        .filter((p) => p.cohorts.length)
        .sort((a, b) => a.name.localeCompare(b.name));
    },
    staleTime: Infinity,
  });

  /*
    Mutations
    ░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░
  */

  const addItemMutation = useMutation({
    mutationFn: (id: string) => network.addLineItem(id),
    onSuccess: (response) => {
      queryClient.setQueryData<TCart>(cartQueryKey, { ...response.data });
      setCheckoutErrorMessage(undefined);
    },
    onError: (err) => logError({ action: "CART_ADD_ITEM", error: err }),
  });

  const removeItemMutation = useMutation({
    mutationFn: (id: string) => network.removeLineItem(id),
    onSuccess: (response) => {
      queryClient.setQueryData<TCart>(cartQueryKey, { ...response.data });
      setCheckoutErrorMessage(undefined);
    },
    onError: (err) => logError({ action: "CART_REMOVE_ITEM", error: err }),
  });

  const applyCouponMutation = useMutation({
    mutationFn: (code: string) => network.addCoupon(code),
    onMutate: () => setCouponError(undefined),
    onSuccess: (cart) => {
      queryClient.setQueryData<TCart>(cartQueryKey, { ...cart });
    },
    onError: (err) => {
      setCouponError(parseCheckoutError(err).message);
      logError({ action: "CART_APPLYING_COUPON", error: err });
    },
  });

  const removeCouponMutation = useMutation({
    mutationFn: (code: string) => network.removeCoupon(code),
    onMutate: () => setCouponError(undefined),
    onSuccess: (cart) => {
      queryClient.setQueryData<TCart>(cartQueryKey, { ...cart });
    },
    onError: (err) => {
      logError({ action: "CART_REMOVE_COUPON", error: err });
    },
  });

  const finalValidateMutation = useMutation({
    mutationFn: () => network.finalValidate(),
    onSuccess: (cart) => {
      queryClient.setQueryData<TCart>(cartQueryKey, { ...cart });
      setCheckoutErrorMessage(undefined);
    },
  });

  const submitAsFreeMutation = useMutation({
    mutationFn: () => network.submitAsComplete(),
  });

  /*
    Apply coupon code from URL on first cart load
    ░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░
  */

  const couponCodeApplied = useRef(false);
  useEffect(() => {
    if (!couponCodeOnLoad || couponCodeApplied.current) return;
    if (!cartQuery.data?.lineItems?.length) return;

    couponCodeApplied.current = true;
    applyCouponMutation.mutate(couponCodeOnLoad);
  }, [
    cartQuery.data?.lineItems?.length,
    couponCodeOnLoad,
    applyCouponMutation,
  ]);

  /*
    Actions
    ░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░
  */

  const submit = () => {
    setIsProcessingPayment(true);
    setCheckoutErrorMessage(undefined);
  };

  const idle = () => {
    setIsProcessingPayment(false);
  };

  const addItem = async (newItemID: string) => {
    return addItemMutation.mutateAsync(newItemID);
  };

  const removeItem = async (itemID: string) => {
    return removeItemMutation.mutateAsync(itemID);
  };

  const applyCoupon = async (code: string) => {
    await applyCouponMutation.mutateAsync(code);
  };

  const removeCoupon = async (code: string) => {
    return removeCouponMutation.mutateAsync(code);
  };

  const finalValidate = async (): Promise<Result<TCart>> => {
    if (!cartQuery.data) {
      return { value: null, error: new Error("Cart data is missing") };
    }

    try {
      const cart = await finalValidateMutation.mutateAsync();
      return { value: cart, error: null };
    } catch (err) {
      const parsed = parseCheckoutError(
        err,
        "An unexpected error occurred. Please try again.",
      );

      setCheckoutErrorMessage(parsed.message);

      if (parsed.type === "validation") {
        queryClient.setQueryData<TCart>(cartQueryKey, (old) =>
          old ? { ...old, errors: parsed.validationErrors } : old,
        );
      }

      if (
        parsed.type === "http_error" ||
        parsed.type === "network_error" ||
        parsed.type === "unknown_error"
      ) {
        logError({ action: "CART_FINAL_VALIDATE_UNEXPECTED", error: err });
      }

      return { value: null, error: new Error(parsed.message) };
    }
  };

  const handleSubmitAsFree = async (): Promise<Result<string>> => {
    if (!cartQuery.data) {
      return { value: null, error: new Error("Cart data is missing") };
    }

    const cartId = cartQuery.data._id;
    setIsProcessingPayment(true);
    setCheckoutErrorMessage(undefined);

    try {
      await submitAsFreeMutation.mutateAsync();
      navigateToPath(`/orders/${cartId}`);
      return { value: cartId, error: null };
    } catch (err) {
      setIsProcessingPayment(false);
      setCheckoutErrorMessage(parseCheckoutError(err).message);
      logError({ action: "CART_SUBMIT_AS_COMPLETE", error: err });
      return { value: null, error: err as Error };
    }
  };

  const navigateToStep = useCallback((s: 0 | 1) => setStep(s), []);

  const updateCart = useCallback(
    (cart: TCart) => {
      // Save endpoints don't return errors — preserve them until finalValidate refreshes
      queryClient.setQueryData<TCart>(cartQueryKey, (old) => ({
        ...cart,
        errors: old?.errors,
      }));
    },
    [queryClient, cartQueryKey],
  );

  const cartStatus = cartQuery.isSuccess
    ? CART_STATUS.SUCCESS
    : cartQuery.isError
      ? CART_STATUS.ERROR
      : auth.currentUser && owner
        ? CART_STATUS.LOADING
        : CART_STATUS.UNSTARTED;

  const cartContext: CartContextType = {
    data: cartQuery.data,
    inventory: inventoryQuery.data ?? [],
    step,
    status: cartStatus,
    isAddingItem: addItemMutation.isPending,
    isSubmitting: isProcessingPayment || submitAsFreeMutation.isPending,
    isApplyingCoupon:
      applyCouponMutation.isPending || removeCouponMutation.isPending,
    isValidating: finalValidateMutation.isPending,
    addItemError: addItemMutation.error
      ? parseCheckoutError(addItemMutation.error).message
      : null,
    error: cartQuery.error ?? undefined,
    couponError,
    checkoutErrorMessage,
    submitAsCompleteDidError: submitAsFreeMutation.isError,
    hasBlockingErrors: Boolean(
      cartQuery.data?.errors?.some((error) => error.level > 0),
    ),
    didError: cartQuery.isError,
    cartIsVisible,
    submit,
    idle,
    addItem,
    removeItem,
    setCartIsVisible,
    applyCoupon,
    removeCoupon,
    finalValidate,
    handleSubmitAsFree,
    navigateToStep,
    updateCart,
    setCouponCodeOnLoad,
  };

  return (
    <CartContext.Provider value={cartContext}>{children}</CartContext.Provider>
  );
};

export const CartProvider = ({
  baseURL,
  children,
  navigateToPath,
}: CartProviderProps) => {
  const [queryClient] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: { retry: false, staleTime: Infinity },
          mutations: { retry: false },
        },
      }),
  );

  /*
    Yes, we're setting the network config on render. This is intentional.
    No big deal.
  */
  network.setNetworkConfig({ baseURL });

  return (
    <QueryClientProvider client={queryClient}>
      <CartProviderInner navigateToPath={navigateToPath}>
        {children}
      </CartProviderInner>
    </QueryClientProvider>
  );
};

export const useCart = () => useContext(CartContext);
